import { AxiosRequestConfig } from 'axios'
import CryptoJs from 'crypto-js'
import _ from 'lodash'

const cacheUrlInstanceMap: any = {}

const ONE_HOUR = 3600000

const MAX_CACHE_LIMIT = 1000

export function generatorCacheId (key: any) {
  return CryptoJs.SHA256(JSON.stringify(key)).toString()
}

function generatorCacheUrlId (url: string, method: string, data?: any) {
  return generatorCacheId({ url, method, data })
}

function getCacheSize () {
  return _.size(_.keys(cacheUrlInstanceMap))
}

function getCache (id: string): CacheUrl | undefined {
  return _.get(cacheUrlInstanceMap, id)
}

function saveCache (id: string, cacheUrlInstance: CacheUrl) {
  const cacheSize = getCacheSize()
  if (cacheSize > MAX_CACHE_LIMIT) {
    // reach cache size limit, then delete first one
    const removeKeys = _.take(_.keys(cacheUrlInstanceMap), cacheSize - MAX_CACHE_LIMIT)
    _.each(removeKeys, (key) => {
      delete cacheUrlInstanceMap[key]
    })
  }

  _.set(cacheUrlInstanceMap, id, cacheUrlInstance)
}

function doCacheUrl (cacheId: string, axios: any, url: string, method: string, data?: any, config?: AxiosRequestConfig, timer = ONE_HOUR) {
  const cacheUrlInstance = getCacheInstance(cacheId)
  return _.invoke(cacheUrlInstance, _.lowerCase(method), axios, url, data, config, timer)
}

function getCacheInstance (cacheId: string) {
  let cacheUrlInstance = getCache(cacheId)
  if (!cacheUrlInstance) {
    cacheUrlInstance = new CacheUrl(cacheId)
    saveCache(cacheId, cacheUrlInstance)
  }
  return cacheUrlInstance
}

export function cacheUrlGet (axios: any, url: string, data?: any, config?: AxiosRequestConfig, timer?: number) {
  const method = 'GET'
  const cacheId = generatorCacheUrlId(url, method, data || _.get(config, 'params'))
  return doCacheUrl(cacheId, axios, url, method, data, config, timer)
}

export function cacheUrlPost (axios: any, url: string, data?: any, config?: AxiosRequestConfig, timer?: number) {
  const method = 'POST'
  const cacheId = generatorCacheUrlId(url, method, data)
  return doCacheUrl(cacheId, axios, url, method, data, config, timer)
}

export function cacheUrlRequest (cacheId: string, requestMethod: any, timer?:number) {
  const cacheUrlInstance = getCacheInstance(cacheId)
  return _.invoke(cacheUrlInstance, 'request', requestMethod, timer)
}

type CacheResultItem = {
  id: string,
  currentTime?: number,
  result?: any
}

export class CacheUrl {
  cacheResult: CacheResultItem
  private lastCall: Promise<any> | undefined

  constructor (id: string) {
    this.cacheResult = {
      id: id || _.uniqueId('CacheUrl')
    }
  }

  private async doRequest (http: any, timer = ONE_HOUR) {
    if (!this.isTimeout(timer)) {
      return this.cacheResult.result
    }
    if (this.lastCall) {
      return this.lastCall
    }
    this.lastCall = http()
    try {
      const result = await this.lastCall
      this.lastCall = undefined
      this.cacheResult.currentTime = new Date().getTime()
      this.cacheResult.result = result
      return result
    } catch (e) {
      this.lastCall = undefined
      throw e
    }
  }

  private isTimeout (timer: number) {
    const lastTime = this.cacheResult.currentTime || 0
    return lastTime + timer < new Date().getTime()
  }

  get (axios: any, url: string, data?: any, config?: AxiosRequestConfig, timer?: number) {
    return this.request(() => {
      return axios.get(url, _.defaults({ params: data }, config))
    }, timer)
  }

  post (axios: any, url: string, data?: any, config?: AxiosRequestConfig, timer?: number) {
    return this.request(() => {
      return axios.post(url, data, config)
    }, timer)
  }

  request (requestMethod: any, timer?: number) {
    return this.doRequest(requestMethod, timer)
  }
}

export const downloadFile = (filePath: string, fileName: string) => {
  // 检查是否为有效的URL
  if (!filePath) {
    console.error('File path is required')
    return
  }

  // 如果是相对路径或绝对路径，直接下载
  if (filePath.startsWith('/') || filePath.startsWith('./') || filePath.startsWith('../')) {
    const link = document.createElement('a')
    link.href = filePath
    link.download = fileName || filePath.split('/').pop() || 'download'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return
  }

  // 如果是完整URL，使用fetch方式下载，避免跨域问题
  fetch(filePath)
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      return response.blob()
    })
    .then(blob => {
      // 创建blob URL
      const blobUrl = window.URL.createObjectURL(blob)

      // 创建一个临时的a标签用于下载
      const link = document.createElement('a')
      link.href = blobUrl
      link.style.display = 'none'

      // 设置下载文件名
      if (fileName) {
        link.download = fileName
      } else {
        // 如果没有指定文件名，尝试从URL中提取
        const urlParts = filePath.split('/')
        const originalFileName = urlParts[urlParts.length - 1]
        // 去除查询参数
        const cleanFileName = originalFileName ? originalFileName.split('?')[0] : 'download'
        link.download = cleanFileName || 'download'
      }

      // 添加到DOM，点击下载，然后移除
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // 释放blob URL
      window.URL.revokeObjectURL(blobUrl)
    })
    .catch(error => {
      console.error('Download failed:', error)
    })
}

// 保持向后兼容，如果需要可以保留旧名称指向新函数
export const downLoadPdfFiles = (filePath: string, fileName: string) => {
  // 检查是否为有效的URL
  if (!filePath) {
    console.error('File path is required')
    return
  }

  // 检查文件扩展名是否为PDF
  const fileExtension = filePath.toLowerCase().split('.').pop()
  if (fileExtension !== 'pdf') {
    alert('文件在网站上不可用，请下载pdf后缀的文件')
    return
  }

  // 如果是相对路径或绝对路径，直接下载
  if (filePath.startsWith('/') || filePath.startsWith('./') || filePath.startsWith('../')) {
    const link = document.createElement('a')
    link.href = filePath
    link.download = fileName || filePath.split('/').pop() || 'download.pdf'
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    return
  }

  // 如果是完整URL，使用fetch方式下载，避免跨域问题
  fetch(filePath)
    .then(response => {
      // 检查响应的Content-Type是否为PDF
      const contentType = response.headers.get('Content-Type')
      if (contentType && !contentType.includes('application/pdf')) {
        throw new Error('Not a PDF file')
      }
      return response.blob()
    })
    .then(blob => {
      // 再次确认blob的类型是PDF
      if (blob.type !== 'application/pdf') {
        alert('文件在网站上不可用，请下载pdf后缀的文件')
        return
      }

      // 创建blob URL
      const blobUrl = window.URL.createObjectURL(blob)
      // 创建一个临时的a标签用于下载
      const link = document.createElement('a')
      link.href = blobUrl
      link.style.display = 'none'
      // 设置下载文件名
      if (fileName) {
        // 确保文件名以.pdf结尾
        const finalFileName = fileName.endsWith('.pdf') ? fileName : fileName + '.pdf'
        link.download = finalFileName
      } else {
        // 如果没有指定文件名，尝试从URL中提取
        const urlParts = filePath.split('/')
        const originalFileName = urlParts[urlParts.length - 1]
        link.download = originalFileName || 'download.pdf'
      }

      // 添加到DOM，点击下载，然后移除
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // 释放blob URL
      window.URL.revokeObjectURL(blobUrl)
    })
    .catch(error => {
      console.error('Download failed:', error)
      alert('文件在网站上不可用，请下载pdf后缀的文件')
    })
}
